import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  ArrowUpIcon,
  ArrowDownIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  PauseCircleIcon,
  CloudArrowUpIcon,
  ChevronRightIcon,
  StarIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolidIcon } from "@heroicons/react/24/solid";
import { useCourseModules } from "../../contexts/CourseContext";
import { Course } from "../../firebase/courseService";
import { CourseChapter } from "../../firebase/courseChapterService";
import { CourseModule } from "../../firebase/courseModuleService";
import { Article } from "../../firebase/articleService";
import { ContentBlock, ContentBlockType, BLOCK_TYPE_LABELS, BLOCK_TYPES, createBlankBlock } from "../../types/courseBlocks";
import { BlockForm } from "../../components/admin/course/BlockEditors";
import BlockRenderer from "../../components/course/BlockRenderer";
import { uploadImageToCloudinary } from "../../services/cloudinaryService";
import { migrateBlogsToArticles } from "../../firebase/migrateBlogsToArticles";
import { WRITING_LABEL_GROUPS, toggleWritingLabel } from "../../constants/writingLabels";
import { ImpactSlide, createImpactSlide, usableImpactSlides } from "../../types/impactSlides";

type View = "courses" | "chapters" | "modules" | "module-editor" | "articles" | "article-editor";
type Section = "courses" | "articles";

const inputClass =
  "w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-sm p-2";
const labelClass = "block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1";

const WritingLabelsField: React.FC<{ labels: string[]; onChange: (labels: string[]) => void }> = ({ labels, onChange }) => (
  <fieldset>
    <legend className={labelClass}>Topic labels</legend>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-md border border-gray-200 dark:border-gray-700 p-3">
      {WRITING_LABEL_GROUPS.map((group) => (
        <div key={group.name}>
          <p className="mb-1.5 text-xs font-semibold text-gray-800 dark:text-gray-200">{group.name}</p>
          <div className="space-y-1">
            {group.labels.map((label) => (
              <label key={label} className="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={labels.includes(label)}
                  onChange={() => onChange(toggleWritingLabel(labels, label))}
                />
                {label}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
    <p className="mt-1 text-xs text-gray-500">Select every topic that applies.</p>
  </fieldset>
);

const ImpactSlidesField: React.FC<{
  slides: ImpactSlide[];
  animated: boolean;
  onChange: (next: { impactSlides: ImpactSlide[]; impactAnimated: boolean }) => void;
}> = ({ slides, animated, onChange }) => {
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const setSlides = (impactSlides: ImpactSlide[]) => onChange({ impactSlides, impactAnimated: animated });
  const updateSlide = (index: number, slide: ImpactSlide) => setSlides(slides.map((s, i) => (i === index ? slide : s)));
  const moveSlide = (index: number, direction: -1 | 1) => {
    const next = [...slides];
    [next[index], next[index + direction]] = [next[index + direction], next[index]];
    setSlides(next);
  };
  const uploadSlideImage = async (index: number, file: File) => {
    const slide = slides[index];
    setUploadingId(slide.id);
    try {
      const url = await uploadImageToCloudinary(file);
      updateSlide(index, { id: slide.id, type: "image", imageUrl: url });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploadingId(null);
    }
  };
  const hasContent = usableImpactSlides(slides).length > 0;

  return (
    <div className="space-y-3 rounded-md border border-amber-300/60 dark:border-amber-500/40 bg-amber-50/60 dark:bg-amber-500/5 p-3">
      <div>
        <p className={labelClass}>Impact slideshow</p>
        <p className="text-xs text-gray-500">
          Slides play in order on the card cover, then the cover image plays last as the payoff.
        </p>
      </div>
      {slides.map((slide, index) => (
        <div key={slide.id} className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase text-gray-500">
              {index + 1}. {slide.type === "text" ? "Text" : "Image"}
            </span>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => moveSlide(index, -1)} disabled={index === 0} className="disabled:opacity-30">
                <ArrowUpIcon className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => moveSlide(index, 1)} disabled={index === slides.length - 1} className="disabled:opacity-30">
                <ArrowDownIcon className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => setSlides(slides.filter((_, i) => i !== index))} className="text-rose-500">
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
          {slide.type === "text" ? (
            <textarea
              className={`${inputClass} h-16`}
              maxLength={140}
              placeholder="e.g. 1,200+ developers shipped their first HTTP server"
              value={slide.text}
              onChange={(e) => updateSlide(index, { ...slide, text: e.target.value })}
            />
          ) : (
            <>
              {slide.imageUrl && <img src={slide.imageUrl} alt="" className="max-h-24 rounded" />}
              <div className="flex items-center gap-2">
                <input
                  className={inputClass}
                  placeholder="Image URL"
                  value={slide.imageUrl}
                  onChange={(e) => updateSlide(index, { ...slide, imageUrl: e.target.value })}
                />
                <label className="inline-flex shrink-0 cursor-pointer items-center gap-1 px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600">
                  <CloudArrowUpIcon className="h-4 w-4" /> {uploadingId === slide.id ? "Uploading…" : "Upload"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploadingId === slide.id}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadSlideImage(index, file);
                      e.target.value = "";
                    }}
                  />
                </label>
              </div>
            </>
          )}
        </div>
      ))}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setSlides([...slides, createImpactSlide("text")])}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600"
        >
          <PlusIcon className="h-4 w-4" /> Text slide
        </button>
        <button
          type="button"
          onClick={() => setSlides([...slides, createImpactSlide("image")])}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600"
        >
          <PlusIcon className="h-4 w-4" /> Image slide
        </button>
      </div>
      <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
        <input
          type="checkbox"
          checked={animated}
          disabled={!hasContent}
          onChange={(e) => onChange({ impactSlides: slides, impactAnimated: e.target.checked })}
        />
        Play impact slideshow on cover
      </label>
    </div>
  );
};

const blankCourse: Omit<Course, "id" | "createdAt" | "updatedAt"> = {
  slug: "",
  title: "",
  tagline: "",
  description: "",
  labels: [],
  status: "draft",
  order: 0,
};

const blankChapter = (courseSlug: string, order: number): Omit<CourseChapter, "id" | "createdAt" | "updatedAt"> => ({
  courseSlug,
  number: "",
  slug: "",
  title: "",
  centralQuestion: "",
  summary: "",
  difficulty: "Foundation",
  readingTime: 10,
  prerequisites: [],
  outcomes: [],
  published: false,
  order,
});

const blankModule = (
  courseSlug: string,
  chapterSlug: string,
  order: number
): Omit<CourseModule, "id" | "createdAt" | "updatedAt"> => ({
  courseSlug,
  chapterSlug,
  number: "1",
  slug: "",
  title: "",
  published: false,
  order,
  blocks: [],
});

const blankArticle = (order: number): Omit<Article, "id" | "createdAt" | "updatedAt"> => ({
  slug: "",
  title: "",
  tagline: "",
  labels: [],
  published: false,
  order,
  blocks: [],
});

const CourseStudio: React.FC = () => {
  const {
    courses,
    chapters,
    courseModules,
    articles,
    loading,
    refreshAll,
    addCourse,
    editCourse,
    removeCourse,
    reorderCoursesList,
    addChapter,
    editChapter,
    removeChapter,
    reorderChapters,
    addCourseModule,
    editCourseModule,
    removeCourseModule,
    reorderModules,
    addArticle,
    editArticle,
    removeArticle,
    reorderArticlesList,
  } = useCourseModules();

  const [view, setView] = useState<View>("courses");
  const section: Section = view === "articles" || view === "article-editor" ? "articles" : "courses";
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [selectedChapterId, setSelectedChapterId] = useState<string | null>(null);
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);

  const [courseDraft, setCourseDraft] = useState<Omit<Course, "id" | "createdAt" | "updatedAt"> | null>(null);
  const [chapterDraft, setChapterDraft] = useState<Omit<CourseChapter, "id" | "createdAt" | "updatedAt"> | null>(null);
  const [moduleBlocksDraft, setModuleBlocksDraft] = useState<ContentBlock[]>([]);
  const [modulePublishedDraft, setModulePublishedDraft] = useState(false);
  const [moduleMetaDraft, setModuleMetaDraft] = useState<{ number: string; slug: string; title: string } | null>(null);
  const [migratingBlogs, setMigratingBlogs] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [coverUploading, setCoverUploading] = useState(false);

  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  const [articleMetaDraft, setArticleMetaDraft] = useState<{
    title: string;
    slug: string;
    tagline: string;
    coverImageUrl?: string;
    coverImageIsPlaceholder?: boolean;
    featured?: boolean;
    impactSlides: ImpactSlide[];
    impactAnimated: boolean;
    labels: string[];
  } | null>(null);
  const [articleBlocksDraft, setArticleBlocksDraft] = useState<ContentBlock[]>([]);
  const [articlePublishedDraft, setArticlePublishedDraft] = useState(false);
  const articleFileInputRef = useRef<HTMLInputElement>(null);
  const [articleCoverUploading, setArticleCoverUploading] = useState(false);

  const selectedCourse = courses.find((c) => c.id === selectedCourseId) || null;
  const selectedChapter = chapters.find((c) => c.id === selectedChapterId) || null;
  const selectedModule = courseModules.find((m) => m.id === selectedModuleId) || null;
  const selectedArticle = articles.find((a) => a.id === selectedArticleId) || null;

  const chaptersForCourse = selectedCourse
    ? chapters.filter((c) => c.courseSlug === selectedCourse.slug).sort((a, b) => a.order - b.order)
    : [];
  const modulesForChapter = selectedChapter
    ? courseModules.filter((m) => m.courseSlug === selectedChapter.courseSlug && m.chapterSlug === selectedChapter.slug).sort((a, b) => a.order - b.order)
    : [];

  const handleMigrateBlogs = async () => {
    if (!window.confirm("This copies every existing blog post into Articles (skipping any slug already migrated). Continue?")) return;
    setMigratingBlogs(true);
    try {
      const { migrated, skipped } = await migrateBlogsToArticles();
      toast.success(`Migrated ${migrated} blog post(s)${skipped ? `, skipped ${skipped} already migrated` : ""}`);
      await refreshAll();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Migration failed");
    } finally {
      setMigratingBlogs(false);
    }
  };

  // ---- Courses level ----

  const startAddCourse = () => {
    setCourseDraft({ ...blankCourse, order: courses.length });
    setSelectedCourseId("new");
  };

  const startEditCourse = (course: Course) => {
    setSelectedCourseId(course.id || null);
    setCourseDraft({
      slug: course.slug,
      title: course.title,
      tagline: course.tagline,
      description: course.description,
      labels: course.labels || [],
      coverImageUrl: course.coverImageUrl,
      coverImageIsPlaceholder: course.coverImageIsPlaceholder,
      status: course.status,
      featured: course.featured,
      impactSlides: course.impactSlides || [],
      impactAnimated: !!course.impactAnimated,
      order: course.order,
    });
  };

  const saveCourse = async () => {
    if (!courseDraft) return;
    try {
      if (selectedCourseId === "new") {
        await addCourse(courseDraft);
        toast.success("Course created");
      } else if (selectedCourseId) {
        await editCourse(selectedCourseId, courseDraft);
        toast.success("Course updated");
      }
      setCourseDraft(null);
      setSelectedCourseId(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save course");
    }
  };

  const handleCoverUpload = async (file: File) => {
    setCoverUploading(true);
    try {
      const url = await uploadImageToCloudinary(file);
      setCourseDraft((prev) => (prev ? { ...prev, coverImageUrl: url, coverImageIsPlaceholder: false } : prev));
    } finally {
      setCoverUploading(false);
    }
  };

  const moveCourse = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    const ordered = [...courses].sort((a, b) => a.order - b.order);
    if (target < 0 || target >= ordered.length) return;
    const ids = ordered.map((c) => c.id!);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    await reorderCoursesList(ids);
  };

  // ---- Chapters level ----

  const openCourse = (course: Course) => {
    setSelectedCourseId(course.id || null);
    setView("chapters");
  };

  const startAddChapter = () => {
    if (!selectedCourse) return;
    setChapterDraft(blankChapter(selectedCourse.slug, chaptersForCourse.length));
    setSelectedChapterId("new");
  };

  const startEditChapter = (chapter: CourseChapter) => {
    setSelectedChapterId(chapter.id || null);
    setChapterDraft({
      courseSlug: chapter.courseSlug,
      number: chapter.number,
      slug: chapter.slug,
      title: chapter.title,
      centralQuestion: chapter.centralQuestion,
      summary: chapter.summary,
      difficulty: chapter.difficulty,
      readingTime: chapter.readingTime,
      prerequisites: chapter.prerequisites,
      outcomes: chapter.outcomes,
      published: chapter.published,
      order: chapter.order,
      lastVerifiedAt: chapter.lastVerifiedAt,
    });
  };

  const saveChapter = async () => {
    if (!chapterDraft) return;
    try {
      if (selectedChapterId === "new") {
        await addChapter(chapterDraft);
        toast.success("Chapter created");
      } else if (selectedChapterId) {
        await editChapter(selectedChapterId, chapterDraft);
        toast.success("Chapter updated");
      }
      setChapterDraft(null);
      setSelectedChapterId(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save chapter");
    }
  };

  const moveChapter = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= chaptersForCourse.length) return;
    const ids = chaptersForCourse.map((c) => c.id!);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    await reorderChapters(ids);
  };

  // ---- Modules level ----

  const openChapter = (chapter: CourseChapter) => {
    setSelectedChapterId(chapter.id || null);
    setView("modules");
  };

  const startAddModule = async () => {
    if (!selectedChapter) return;
    const payload = blankModule(selectedChapter.courseSlug, selectedChapter.slug, modulesForChapter.length);
    const id = await addCourseModule(payload);
    const created = { ...payload, id };
    openModuleEditor(created as CourseModule);
  };

  const openModuleEditor = (module: CourseModule) => {
    setSelectedModuleId(module.id || null);
    setModuleBlocksDraft(module.blocks);
    setModulePublishedDraft(module.published);
    setModuleMetaDraft({ number: module.number, slug: module.slug, title: module.title });
    setView("module-editor");
  };

  const moveModule = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= modulesForChapter.length) return;
    const ids = modulesForChapter.map((m) => m.id!);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    await reorderModules(ids);
  };

  // ---- Block editor ----

  const addBlock = (type: ContentBlockType) => {
    setModuleBlocksDraft((prev) => [...prev, createBlankBlock(type)]);
  };

  const updateBlock = (index: number, block: ContentBlock) => {
    setModuleBlocksDraft((prev) => prev.map((b, i) => (i === index ? block : b)));
  };

  const removeBlock = (index: number) => {
    setModuleBlocksDraft((prev) => prev.filter((_, i) => i !== index));
  };

  const moveBlock = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= moduleBlocksDraft.length) return;
    setModuleBlocksDraft((prev) => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const saveModule = async (publish: boolean) => {
    if (!selectedModuleId || !moduleMetaDraft) return;
    try {
      await editCourseModule(selectedModuleId, {
        ...moduleMetaDraft,
        blocks: moduleBlocksDraft,
        published: publish,
      });
      setModulePublishedDraft(publish);
      toast.success(publish ? "Published" : "Saved as draft");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save module");
    }
  };

  // ---- Articles ----

  const startAddArticle = async () => {
    const payload = blankArticle(articles.length);
    const id = await addArticle(payload);
    openArticleEditor({ ...payload, id } as Article);
  };

  const openArticleEditor = (article: Article) => {
    setSelectedArticleId(article.id || null);
    setArticleMetaDraft({
      title: article.title,
      slug: article.slug,
      tagline: article.tagline,
      coverImageUrl: article.coverImageUrl,
      coverImageIsPlaceholder: article.coverImageIsPlaceholder,
      featured: article.featured,
      impactSlides: article.impactSlides || [],
      impactAnimated: !!article.impactAnimated,
      labels: article.labels || [],
    });
    setArticleBlocksDraft(article.blocks);
    setArticlePublishedDraft(article.published);
    setView("article-editor");
  };

  const moveArticle = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    const ordered = [...articles].sort((a, b) => a.order - b.order);
    if (target < 0 || target >= ordered.length) return;
    const ids = ordered.map((a) => a.id!);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    await reorderArticlesList(ids);
  };

  const handleArticleCoverUpload = async (file: File) => {
    setArticleCoverUploading(true);
    try {
      const url = await uploadImageToCloudinary(file);
      setArticleMetaDraft((prev) => (prev ? { ...prev, coverImageUrl: url, coverImageIsPlaceholder: false } : prev));
    } finally {
      setArticleCoverUploading(false);
    }
  };

  const addArticleBlock = (type: ContentBlockType) => {
    setArticleBlocksDraft((prev) => [...prev, createBlankBlock(type)]);
  };

  const updateArticleBlock = (index: number, block: ContentBlock) => {
    setArticleBlocksDraft((prev) => prev.map((b, i) => (i === index ? block : b)));
  };

  const removeArticleBlock = (index: number) => {
    setArticleBlocksDraft((prev) => prev.filter((_, i) => i !== index));
  };

  const moveArticleBlock = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= articleBlocksDraft.length) return;
    setArticleBlocksDraft((prev) => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const saveArticle = async (publish: boolean) => {
    if (!selectedArticleId || !articleMetaDraft) return;
    try {
      await editArticle(selectedArticleId, {
        ...articleMetaDraft,
        blocks: articleBlocksDraft,
        published: publish,
      });
      setArticlePublishedDraft(publish);
      toast.success(publish ? "Published" : "Saved as draft");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save article");
    }
  };

  // ---- Render ----

  if (loading && courses.length === 0) {
    return <div className="p-6">Loading course studio…</div>;
  }

  return (
    <div className="p-6">
      <div className="flex items-center gap-2 mb-4 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => { setView("courses"); setSelectedChapterId(null); setSelectedModuleId(null); }}
          className={`px-3 py-2 text-sm font-medium border-b-2 ${
            section === "courses" ? "border-primary-600 text-primary-600" : "border-transparent text-gray-500"
          }`}
        >
          Courses
        </button>
        <button
          onClick={() => { setView("articles"); setSelectedArticleId(null); }}
          className={`px-3 py-2 text-sm font-medium border-b-2 ${
            section === "articles" ? "border-primary-600 text-primary-600" : "border-transparent text-gray-500"
          }`}
        >
          Articles
        </button>
      </div>

      {section === "courses" && (
        <nav className="flex items-center gap-1 text-sm text-gray-500 mb-4">
          <button onClick={() => { setView("courses"); setSelectedChapterId(null); setSelectedModuleId(null); }} className="hover:underline">
            Courses
          </button>
          {selectedCourse && (
            <>
              <ChevronRightIcon className="h-3 w-3" />
              <button onClick={() => { setView("chapters"); setSelectedModuleId(null); }} className="hover:underline">
                {selectedCourse.title}
              </button>
            </>
          )}
          {selectedChapter && view !== "chapters" && (
            <>
              <ChevronRightIcon className="h-3 w-3" />
              <button onClick={() => { setView("modules"); setSelectedModuleId(null); }} className="hover:underline">
                {selectedChapter.title}
              </button>
            </>
          )}
          {selectedModule && view === "module-editor" && (
            <>
              <ChevronRightIcon className="h-3 w-3" />
              <span>{selectedModule.title || "New module"}</span>
            </>
          )}
        </nav>
      )}

      {section === "articles" && (
        <nav className="flex items-center gap-1 text-sm text-gray-500 mb-4">
          <button onClick={() => { setView("articles"); setSelectedArticleId(null); }} className="hover:underline">
            Articles
          </button>
          {selectedArticle && view === "article-editor" && (
            <>
              <ChevronRightIcon className="h-3 w-3" />
              <span>{selectedArticle.title || "New article"}</span>
            </>
          )}
        </nav>
      )}

      {view === "courses" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-4 gap-2">
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">Courses</h1>
              <div className="flex gap-2">
                <button
                  onClick={startAddCourse}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700"
                >
                  <PlusIcon className="h-4 w-4" /> New course
                </button>
              </div>
            </div>

            <ul className="space-y-2">
              {[...courses].sort((a, b) => a.order - b.order).map((course, index) => (
                <li
                  key={course.id}
                  className="p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 cursor-pointer"
                  onClick={() => openCourse(course)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {course.coverImageUrl && (
                        <img src={course.coverImageUrl} alt="" className="h-10 w-16 object-cover rounded" />
                      )}
                      <span className="font-medium text-gray-900 dark:text-white">{course.title}</span>
                    </div>
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        title={course.status === "published" ? "Published" : "Draft"}
                        onClick={() => editCourse(course.id!, { status: course.status === "published" ? "draft" : "published" })}
                        className={course.status === "published" ? "text-emerald-600" : "text-gray-400"}
                      >
                        {course.status === "published" ? <CheckCircleIcon className="h-5 w-5" /> : <PauseCircleIcon className="h-5 w-5" />}
                      </button>
                      <button
                        title={course.featured ? "Featured" : "Not featured"}
                        onClick={() => editCourse(course.id!, { featured: !course.featured })}
                        className={course.featured ? "text-amber-500" : "text-gray-400"}
                      >
                        {course.featured ? <StarSolidIcon className="h-5 w-5" /> : <StarIcon className="h-5 w-5" />}
                      </button>
                      <button onClick={() => moveCourse(index, -1)} disabled={index === 0} className="disabled:opacity-30">
                        <ArrowUpIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => moveCourse(index, 1)} disabled={index === courses.length - 1} className="disabled:opacity-30">
                        <ArrowDownIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => startEditCourse(course)} className="text-primary-600 text-sm">Edit</button>
                      <button
                        onClick={async () => {
                          if (window.confirm("Delete this course?")) await removeCourse(course.id!);
                        }}
                        className="text-rose-500"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {courseDraft && (
            <div className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 p-4 space-y-3">
              <h2 className="font-semibold text-gray-900 dark:text-white">
                {selectedCourseId === "new" ? "New course" : "Edit course"}
              </h2>
              <div>
                <label className={labelClass}>Title</label>
                <input className={inputClass} value={courseDraft.title} onChange={(e) => setCourseDraft({ ...courseDraft, title: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>Slug</label>
                <input className={inputClass} value={courseDraft.slug} onChange={(e) => setCourseDraft({ ...courseDraft, slug: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>Tagline</label>
                <input className={inputClass} value={courseDraft.tagline} onChange={(e) => setCourseDraft({ ...courseDraft, tagline: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>Description</label>
                <textarea className={`${inputClass} h-24`} value={courseDraft.description} onChange={(e) => setCourseDraft({ ...courseDraft, description: e.target.value })} />
              </div>
              <WritingLabelsField
                labels={courseDraft.labels || []}
                onChange={(labels) => setCourseDraft({ ...courseDraft, labels })}
              />
              <div>
                <label className={labelClass}>Cover image</label>
                {courseDraft.coverImageUrl && <img src={courseDraft.coverImageUrl} alt="" className="max-h-32 rounded mb-2" />}
                <div className="flex items-center gap-2">
                  <input
                    className={inputClass}
                    placeholder="Image URL"
                    value={courseDraft.coverImageUrl || ""}
                    onChange={(e) => setCourseDraft({ ...courseDraft, coverImageUrl: e.target.value })}
                  />
                  <button
                    type="button"
                    disabled={coverUploading}
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50"
                  >
                    <CloudArrowUpIcon className="h-4 w-4" /> {coverUploading ? "Uploading…" : "Upload"}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleCoverUpload(file);
                      e.target.value = "";
                    }}
                  />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={courseDraft.status === "published"}
                    onChange={(e) => setCourseDraft({ ...courseDraft, status: e.target.checked ? "published" : "draft" })}
                  />
                  Published
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={!!courseDraft.featured}
                    onChange={(e) => setCourseDraft({ ...courseDraft, featured: e.target.checked })}
                  />
                  Featured
                </label>
              </div>
              {courseDraft.featured && (
                <ImpactSlidesField
                  slides={courseDraft.impactSlides || []}
                  animated={!!courseDraft.impactAnimated}
                  onChange={(next) => setCourseDraft({ ...courseDraft, ...next })}
                />
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => { setCourseDraft(null); setSelectedCourseId(null); }} className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600">
                  Cancel
                </button>
                <button onClick={saveCourse} className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700">
                  Save
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {view === "chapters" && selectedCourse && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">Chapters</h1>
              <button onClick={startAddChapter} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700">
                <PlusIcon className="h-4 w-4" /> New chapter
              </button>
            </div>
            <ul className="space-y-2">
              {chaptersForCourse.map((chapter, index) => (
                <li key={chapter.id} className="p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 cursor-pointer" onClick={() => openChapter(chapter)}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900 dark:text-white">{chapter.number}. {chapter.title}</span>
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => editChapter(chapter.id!, { published: !chapter.published })}
                        className={chapter.published ? "text-emerald-600" : "text-gray-400"}
                      >
                        {chapter.published ? <CheckCircleIcon className="h-5 w-5" /> : <PauseCircleIcon className="h-5 w-5" />}
                      </button>
                      <button onClick={() => moveChapter(index, -1)} disabled={index === 0} className="disabled:opacity-30">
                        <ArrowUpIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => moveChapter(index, 1)} disabled={index === chaptersForCourse.length - 1} className="disabled:opacity-30">
                        <ArrowDownIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => startEditChapter(chapter)} className="text-primary-600 text-sm">Edit</button>
                      <button
                        onClick={async () => {
                          if (window.confirm("Delete this chapter?")) await removeChapter(chapter.id!);
                        }}
                        className="text-rose-500"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {chapterDraft && (
            <div className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 p-4 space-y-3">
              <h2 className="font-semibold text-gray-900 dark:text-white">
                {selectedChapterId === "new" ? "New chapter" : "Edit chapter"}
              </h2>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelClass}>Number</label>
                  <input className={inputClass} value={chapterDraft.number} onChange={(e) => setChapterDraft({ ...chapterDraft, number: e.target.value })} />
                </div>
                <div>
                  <label className={labelClass}>Slug</label>
                  <input className={inputClass} value={chapterDraft.slug} onChange={(e) => setChapterDraft({ ...chapterDraft, slug: e.target.value })} />
                </div>
              </div>
              <div>
                <label className={labelClass}>Title</label>
                <input className={inputClass} value={chapterDraft.title} onChange={(e) => setChapterDraft({ ...chapterDraft, title: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>Central question</label>
                <input className={inputClass} value={chapterDraft.centralQuestion} onChange={(e) => setChapterDraft({ ...chapterDraft, centralQuestion: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>Summary</label>
                <textarea className={`${inputClass} h-20`} value={chapterDraft.summary} onChange={(e) => setChapterDraft({ ...chapterDraft, summary: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelClass}>Difficulty</label>
                  <select className={inputClass} value={chapterDraft.difficulty} onChange={(e) => setChapterDraft({ ...chapterDraft, difficulty: e.target.value as CourseChapter["difficulty"] })}>
                    <option value="Foundation">Foundation</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Reading time (min)</label>
                  <input type="number" className={inputClass} value={chapterDraft.readingTime} onChange={(e) => setChapterDraft({ ...chapterDraft, readingTime: Number(e.target.value) || 0 })} />
                </div>
              </div>
              <div>
                <label className={labelClass}>Outcomes (one per line)</label>
                <textarea
                  className={`${inputClass} h-20`}
                  value={chapterDraft.outcomes.join("\n")}
                  onChange={(e) => setChapterDraft({ ...chapterDraft, outcomes: e.target.value.split("\n").filter(Boolean) })}
                />
              </div>
              <div>
                <label className={labelClass}>Prerequisites (one per line)</label>
                <textarea
                  className={`${inputClass} h-16`}
                  value={chapterDraft.prerequisites.join("\n")}
                  onChange={(e) => setChapterDraft({ ...chapterDraft, prerequisites: e.target.value.split("\n").filter(Boolean) })}
                />
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={chapterDraft.published} onChange={(e) => setChapterDraft({ ...chapterDraft, published: e.target.checked })} />
                Published
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => { setChapterDraft(null); setSelectedChapterId(null); }} className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600">
                  Cancel
                </button>
                <button onClick={saveChapter} className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700">
                  Save
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {view === "modules" && selectedChapter && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Modules</h1>
            <button onClick={startAddModule} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700">
              <PlusIcon className="h-4 w-4" /> New module
            </button>
          </div>
          <ul className="space-y-2 max-w-xl">
            {modulesForChapter.map((module, index) => (
              <li key={module.id} className="p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 cursor-pointer" onClick={() => openModuleEditor(module)}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900 dark:text-white">{module.number}. {module.title || "Untitled module"}</span>
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => editCourseModule(module.id!, { published: !module.published })}
                      className={module.published ? "text-emerald-600" : "text-gray-400"}
                    >
                      {module.published ? <CheckCircleIcon className="h-5 w-5" /> : <PauseCircleIcon className="h-5 w-5" />}
                    </button>
                    <button onClick={() => moveModule(index, -1)} disabled={index === 0} className="disabled:opacity-30">
                      <ArrowUpIcon className="h-4 w-4" />
                    </button>
                    <button onClick={() => moveModule(index, 1)} disabled={index === modulesForChapter.length - 1} className="disabled:opacity-30">
                      <ArrowDownIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={async () => {
                        if (window.confirm("Delete this module?")) await removeCourseModule(module.id!);
                      }}
                      className="text-rose-500"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">{module.blocks.length} block(s)</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {view === "module-editor" && selectedModule && moduleMetaDraft && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <input className={inputClass} placeholder="Number" value={moduleMetaDraft.number} onChange={(e) => setModuleMetaDraft({ ...moduleMetaDraft, number: e.target.value })} />
              <input className={inputClass} placeholder="Slug" value={moduleMetaDraft.slug} onChange={(e) => setModuleMetaDraft({ ...moduleMetaDraft, slug: e.target.value })} />
              <input className={inputClass} placeholder="Title" value={moduleMetaDraft.title} onChange={(e) => setModuleMetaDraft({ ...moduleMetaDraft, title: e.target.value })} />
            </div>

            <div className="space-y-3">
              {moduleBlocksDraft.map((block, index) => (
                <div key={block.id} className="border border-gray-200 dark:border-gray-700 rounded-md p-3 bg-white dark:bg-gray-800">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold uppercase text-gray-500">{BLOCK_TYPE_LABELS[block.type]}</span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => moveBlock(index, -1)} disabled={index === 0} className="disabled:opacity-30">
                        <ArrowUpIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => moveBlock(index, 1)} disabled={index === moduleBlocksDraft.length - 1} className="disabled:opacity-30">
                        <ArrowDownIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => removeBlock(index)} className="text-rose-500">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <BlockForm block={block} onChange={(updated) => updateBlock(index, updated)} />
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              {BLOCK_TYPES.map((type) => (
                <button
                  key={type}
                  onClick={() => addBlock(type)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600"
                >
                  <PlusIcon className="h-4 w-4" /> {BLOCK_TYPE_LABELS[type]}
                </button>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => setView("modules")} className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600">
                Back
              </button>
              <button onClick={() => saveModule(false)} className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600">
                Save as draft
              </button>
              <button onClick={() => saveModule(true)} className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700">
                Publish
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Status: {modulePublishedDraft ? "Published" : "Draft"}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 p-4 sticky top-4 self-start">
            <p className="text-xs font-semibold uppercase text-gray-500 mb-2">Live preview</p>
            <div className="course-shell">
              <div className="lesson-content">
                <BlockRenderer blocks={moduleBlocksDraft} scopeId="preview" />
              </div>
            </div>
          </div>
        </div>
      )}

      {view === "articles" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Articles</h1>
            <div className="flex gap-2">
              <button
                onClick={handleMigrateBlogs}
                disabled={migratingBlogs}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50"
              >
                {migratingBlogs ? "Migrating…" : "Migrate blogs to articles"}
              </button>
              <button onClick={startAddArticle} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700">
                <PlusIcon className="h-4 w-4" /> New article
              </button>
            </div>
          </div>
          <ul className="space-y-2 max-w-xl">
            {[...articles].sort((a, b) => a.order - b.order).map((article, index) => (
              <li
                key={article.id}
                className="p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 cursor-pointer"
                onClick={() => openArticleEditor(article)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {article.coverImageUrl && (
                      <img src={article.coverImageUrl} alt="" className="h-10 w-16 object-cover rounded" />
                    )}
                    <span className="font-medium text-gray-900 dark:text-white">{article.title || "Untitled article"}</span>
                  </div>
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => editArticle(article.id!, { published: !article.published })}
                      className={article.published ? "text-emerald-600" : "text-gray-400"}
                    >
                      {article.published ? <CheckCircleIcon className="h-5 w-5" /> : <PauseCircleIcon className="h-5 w-5" />}
                    </button>
                    <button
                      title={article.featured ? "Featured" : "Not featured"}
                      onClick={() => editArticle(article.id!, { featured: !article.featured })}
                      className={article.featured ? "text-amber-500" : "text-gray-400"}
                    >
                      {article.featured ? <StarSolidIcon className="h-5 w-5" /> : <StarIcon className="h-5 w-5" />}
                    </button>
                    <button onClick={() => moveArticle(index, -1)} disabled={index === 0} className="disabled:opacity-30">
                      <ArrowUpIcon className="h-4 w-4" />
                    </button>
                    <button onClick={() => moveArticle(index, 1)} disabled={index === articles.length - 1} className="disabled:opacity-30">
                      <ArrowDownIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={async () => {
                        if (window.confirm("Delete this article?")) await removeArticle(article.id!);
                      }}
                      className="text-rose-500"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">{article.blocks.length} block(s)</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {view === "article-editor" && selectedArticle && articleMetaDraft && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <div className="space-y-4">
            <div>
              <label className={labelClass}>Title</label>
              <input
                className={inputClass}
                value={articleMetaDraft.title}
                onChange={(e) => setArticleMetaDraft({ ...articleMetaDraft, title: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelClass}>Slug</label>
                <input
                  className={inputClass}
                  value={articleMetaDraft.slug}
                  onChange={(e) => setArticleMetaDraft({ ...articleMetaDraft, slug: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Tagline</label>
              <input
                className={inputClass}
                value={articleMetaDraft.tagline}
                onChange={(e) => setArticleMetaDraft({ ...articleMetaDraft, tagline: e.target.value })}
              />
            </div>
            <WritingLabelsField
              labels={articleMetaDraft.labels}
              onChange={(labels) => setArticleMetaDraft({ ...articleMetaDraft, labels })}
            />
            <div>
              <label className={labelClass}>Cover image</label>
              {articleMetaDraft.coverImageUrl && (
                <img src={articleMetaDraft.coverImageUrl} alt="" className="max-h-32 rounded mb-2" />
              )}
              <div className="flex items-center gap-2">
                <input
                  className={inputClass}
                  placeholder="Image URL"
                  value={articleMetaDraft.coverImageUrl || ""}
                  onChange={(e) => setArticleMetaDraft({ ...articleMetaDraft, coverImageUrl: e.target.value })}
                />
                <button
                  type="button"
                  disabled={articleCoverUploading}
                  onClick={() => articleFileInputRef.current?.click()}
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50"
                >
                  <CloudArrowUpIcon className="h-4 w-4" /> {articleCoverUploading ? "Uploading…" : "Upload"}
                </button>
                <input
                  ref={articleFileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleArticleCoverUpload(file);
                    e.target.value = "";
                  }}
                />
              </div>
            </div>

            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={!!articleMetaDraft.featured}
                onChange={(e) => setArticleMetaDraft({ ...articleMetaDraft, featured: e.target.checked })}
              />
              Featured
            </label>
            {articleMetaDraft.featured && (
              <ImpactSlidesField
                slides={articleMetaDraft.impactSlides}
                animated={articleMetaDraft.impactAnimated}
                onChange={(next) => setArticleMetaDraft({ ...articleMetaDraft, ...next })}
              />
            )}

            <div className="space-y-3">
              {articleBlocksDraft.map((block, index) => (
                <div key={block.id} className="border border-gray-200 dark:border-gray-700 rounded-md p-3 bg-white dark:bg-gray-800">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold uppercase text-gray-500">{BLOCK_TYPE_LABELS[block.type]}</span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => moveArticleBlock(index, -1)} disabled={index === 0} className="disabled:opacity-30">
                        <ArrowUpIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => moveArticleBlock(index, 1)} disabled={index === articleBlocksDraft.length - 1} className="disabled:opacity-30">
                        <ArrowDownIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => removeArticleBlock(index)} className="text-rose-500">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <BlockForm block={block} onChange={(updated) => updateArticleBlock(index, updated)} />
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              {BLOCK_TYPES.map((type) => (
                <button
                  key={type}
                  onClick={() => addArticleBlock(type)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600"
                >
                  <PlusIcon className="h-4 w-4" /> {BLOCK_TYPE_LABELS[type]}
                </button>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => setView("articles")} className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600">
                Back
              </button>
              <button onClick={() => saveArticle(false)} className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600">
                Save as draft
              </button>
              <button onClick={() => saveArticle(true)} className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700">
                Publish
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Status: {articlePublishedDraft ? "Published" : "Draft"}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 p-4 sticky top-4 self-start">
            <p className="text-xs font-semibold uppercase text-gray-500 mb-2">Live preview</p>
            <div className="course-shell">
              <div className="lesson-content">
                <BlockRenderer blocks={articleBlocksDraft} scopeId="preview-article" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseStudio;
